import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { setCustomerOverlayNav } from '@/store/actions';
import { BodyThree } from '@/styles/theme/default/typography';
import FullHeadMenuDropdown from './navBottom/FullHeadMenuDropdown';
import menuItems from '../menuItems';
import getImageUrl from '@/utils/images';
import RebrandedCartButton from '../common/RebrandedCartButton';
import TheInsideLogo from '@/common/svgs/TheInsideLogo';
import LoginButton from '../common/LoginButton';

const bigDesktop = '1399px';

function RebrandedNavigation({ setShowSearch }) {
  const customerOverlayNav = useSelector(
    state => state.Global.customerOverlayNav
  );

  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const dispatch = useDispatch();

  const handleOverlay = bool => {
    dispatch(setCustomerOverlayNav(bool));
  };

  useEffect(() => {
    handleOverlay(shouldDropDown);
  }, [hoveredItem]);

  useEffect(() => {
    setHoveredItem(null);
  }, [location.pathname, location.search]);

  const MenuItem = ({ to, label, name, className }) =>
    <ListItem
      onMouseEnter={() => setHoveredItem(name)}
      className={
        hoveredItem === name ? 'hovered' : ''
      }
    >
      <BodyThree as="span">
        <Link className={ className } to={ to || '' }>
          { label }
        </Link>
      </BodyThree>
    </ListItem>;

  const shouldDropDown = menuItems
    .filter(({ dropdown }) => dropdown)
    .some(({ name }) => name === hoveredItem);

  const isSingleItems = ['collaborations', 'houseTours'].includes(hoveredItem);

  return (
    <nav>
      <CustomerOverlay
        className={`customer-overlay-nav ${
          customerOverlayNav ? 'overlay-on' : ''
        }`}
      />

      <MenuItems onMouseLeave={() => setHoveredItem(null)}>
        <div className="nav-container">
            <ul className="left-side">
              { menuItems.slice(0,6).map(item =>
                  <MenuItem key={item.name} {...item} />
              )}
            </ul>

            <Link to="/" aria-label="The Inside">
              <TheInsideLogo className="logo" />
            </Link>

            <div className="right-side">
              <ul>
                { menuItems.slice(6).map(item =>
                    <MenuItem key={item.name} {...item} />
                )}
              </ul>
              <div className="user-menu">
                <IconMenuItem
                  aria-label={'Search button'}
                  onClick={() => setShowSearch(true)}
                  img="nav/search_icon.svg"
                  className="icon-menu-item"
                />

                <LoginButton className="icon-menu-item"/>

                <RebrandedCartButton />
              </div>
            </div>

        </div>

        <FullHeadMenuDropdown
          itemsWithDropDown={shouldDropDown}
          hoveredItem={hoveredItem}
          isSingleItems={isSingleItems}
        />
      </MenuItems>
    </nav>
  );
}

function IconMenuItem({ onClick, img, className, href }) {
  return (
    <div>
      <a onClick={onClick} className={className} href={href}>
        <img src={getImageUrl(img)} alt={''}/>
      </a>
    </div>
  )
}

const CustomerOverlay = styled.div`
  background: rgba(0, 0, 0, 0.75);
  bottom: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  transition: opacity 0.8s ease-out;
  z-index: 1001;

  &.overlay-on {
    opacity: 1;
    transition: opacity 0.3s ease-out;
  }
`;

const ListItem = styled.li`
  cursor: pointer;
  padding-bottom: 12px;
  display: flex;
  justify-content: center;
  &.hovered {
    text-decoration: underline;
  }
`;

const MenuItems = styled.div`
  background: white;
  border-bottom: 1px solid #ececec;
  z-index: 1002;
  position: relative;

  .nav-container {
    display: flex;
    align-items: flex-end;
    width: 100%;

    .caramel {
      color: ${({ theme }) => theme.colors.caramel};
    }

    ul {
      padding: 0;
    }

    .bold {
      font-weight: 500;
    }

    a {
      text-decoration: none;
      color: ${({ theme }) => theme.colors.charcoal};
    }

    .logo {
      margin: 33px 40px 10px;
      max-width: 200px;
      height: auto;
      transform: translate(0, -10px);
      @media (max-width: ${bigDesktop}) {
        max-width: 163px;
        margin: 33px 20px 10px;
      }
    }

    .left-side, .right-side {
      display: flex;
      flex: 1;
    }

    .left-side {
      justify-content: flex-end;
      gap: 32px;
      @media (max-width: ${bigDesktop}) {
        gap: 22px;
      }
    }

    .right-side {
      justify-content: space-between;
      ul {
        display: flex;
        gap: 21px;
        gap: 30px;
      }
    }
  }

  .user-menu {
    display: flex;
    gap: 21px;
    justify-content: space-between;
    margin-right: 47px;
    @media (max-width: ${bigDesktop}) {
      margin-right: 20px;
    }

    .icon-menu-item {
      cursor: pointer;
      img {
        width: 17px;
      }
    }
  }
`;

export default RebrandedNavigation;
